import {
  CompleteKundali,
  PlanetName,
  ZodiacSign,
  Language,
  DashaPeriod
} from '../../types/kundali';
import {
  PredictiveEventType,
  PredictiveEventWindow,
  NativeLifeMilestone,
  DashaAtMilestone,
  CalibrationResult,
  CalibrationSummary,
  LongitudinalProfile,
  RemedyAdherenceRecord,
  RemedyAdherenceStatus,
  ConsultationSession,
  ConsultationMemory,
  ConsultationMemoryItem,
  ConsultationMemoryItemType,
  PredictionOutcomeRecord,
  PredictionOutcomeStatus,
  V519Score,
  V519DateString
} from '../../types/v519';
import { ZODIAC_LORDS } from '../vedicEngine';
import { getEventRuleDefinition } from './eventPredictionEngine';

/* ==========================================================================
   VEDANG JYOTISHAM V5.19 — LONGITUDINAL INTELLIGENCE ENGINE
   --------------------------------------------------------------------------
   Step 4 Module A:
   Manages historical life milestones, deterministic dasha calibration,
   remedy adherence logs, consultation continuity, and prediction validation.

   ARCHITECTURAL PRINCIPLES:
   - Absolute immutability: zero direct object mutations.
   - Pure, deterministic, side-effect free transformations.
   - Data privacy: zero external network calls, zero LLM calls, zero PII logging.
   - Distinct differentiation between:
       1. HISTORICAL CALIBRATION (Past events ↔ Astrological rules)
       2. PREDICTION VALIDATION (Future forecasts ↔ Later observed outcomes)
   - "Astrological Calibration Score" strictly measures rule correspondence,
     never claiming empirical statistical proof of predictive causation.
   ========================================================================== */

const ALL_SIGNS: ZodiacSign[] = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

function getSignIndex(sign: ZodiacSign): number {
  const idx = (ALL_SIGNS || []).indexOf(sign);
  return idx >= 0 ? idx : 0;
}

function getPlanetOwnedHouses(planet: PlanetName, lagnaSign: ZodiacSign): number[] {
  const lagnaIdx = getSignIndex(lagnaSign);
  const owned: number[] = [];
  for (let h = 1; h <= 12; h++) {
    const signAtHouse = ALL_SIGNS[(lagnaIdx + (h - 1)) % 12];
    if (ZODIAC_LORDS[signAtHouse] === planet) {
      owned.push(h);
    }
  }
  return owned;
}

/* ==========================================================================
   1. DATE VALIDATION & FORMAT CHECKING
   ========================================================================== */

/**
 * Strictly validates that a date string is in valid YYYY-MM-DD format with real calendar dates.
 * Rejects invalid syntax, leap year anomalies, and non-existent days (e.g. Feb 30).
 */
export function isValidDateFormat(dateStr: string): boolean {
  if (!dateStr || typeof dateStr !== 'string') return false;
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const dateObj = new Date(year, month - 1, day);
  return (
    dateObj.getFullYear() === year &&
    dateObj.getMonth() === month - 1 &&
    dateObj.getDate() === day
  );
}

function generateDeterministicId(prefix: string, seed: string, index: number): string {
  let hash = 0;
  const str = `${seed}_${index}`;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return `${prefix}_${Math.abs(hash).toString(16)}`;
}

/* ==========================================================================
   2. DASHA IDENTIFICATION AT HISTORICAL DATE
   ========================================================================== */

/**
 * Identifies the Vimshottari Mahadasha, Antardasha, and Pratyantardasha active at any date.
 * Consumes frozen V5.17 kundali.dashas data as the authoritative calculation source.
 */
export function findDashaAtDate(kundali: CompleteKundali, dateStr: string): DashaAtMilestone | null {
  if (!isValidDateFormat(dateStr) || !kundali.dashas || kundali.dashas.length === 0) {
    return null;
  }

  const target = new Date(dateStr).getTime();
  if (isNaN(target)) return null;

  for (const md of kundali.dashas) {
    const mdStart = new Date(md.startDate).getTime();
    const mdEnd = new Date(md.endDate).getTime();

    if (target >= mdStart && target <= mdEnd) {
      const mahadashaLord = md.planet;
      let antardashaLord: PlanetName | string = md.planet;
      let pratyantardashaLord: PlanetName | string | undefined = undefined;

      if (md.antardashas && md.antardashas.length > 0) {
        for (const ad of md.antardashas) {
          const adStart = new Date(ad.startDate).getTime();
          const adEnd = new Date(ad.endDate).getTime();
          if (target >= adStart && target <= adEnd) {
            antardashaLord = ad.planet;
            if (ad.antardashas && ad.antardashas.length > 0) {
              for (const pd of ad.antardashas) {
                const pdStart = new Date(pd.startDate).getTime();
                const pdEnd = new Date(pd.endDate).getTime();
                if (target >= pdStart && target <= pdEnd) {
                  pratyantardashaLord = pd.planet;
                  break;
                }
              }
            }
            break;
          }
        }
      }

      return {
        mahadasha: mahadashaLord,
        antardasha: antardashaLord,
        pratyantardasha: pratyantardashaLord
      };
    }
  }

  return null;
}

/* ==========================================================================
   3. HISTORICAL MILESTONE CALIBRATION ENGINE
   ========================================================================== */

/**
 * Calibrates a single recorded historical life milestone against classical astrological rules.
 * Calculates the Astrological Calibration Score representing rule correspondence.
 */
export function calibrateMilestone(
  kundali: CompleteKundali,
  milestone: NativeLifeMilestone
): CalibrationResult {
  if (!isValidDateFormat(milestone.date)) {
    return {
      milestoneId: milestone.id,
      eventType: milestone.eventType,
      observedDate: milestone.date,
      expectedIndicatorsEn: ['Valid calendar date required for dasha alignment.'],
      expectedIndicatorsHi: ['दशा मिलान के लिए वैध कैलेंडर तिथि आवश्यक है।'],
      matchedIndicatorsEn: [],
      matchedIndicatorsHi: [],
      unmatchedIndicatorsEn: ['Invalid or unparseable milestone date provided.'],
      unmatchedIndicatorsHi: ['प्रदान की गई घटना की तिथि अमान्य या अस्पष्ट है।'],
      calibrationAccuracyScore: 30,
      confidence: 'LOW',
      methodology: 'Deterministic Astrological Rule Matching (V5.19)',
      limitations: 'Cannot perform astrological calibration on an invalid date.'
    };
  }

  const rule = getEventRuleDefinition(milestone.eventType);
  const lagnaSign = kundali.houses[0]?.sign || 'Aries';
  const dashaAtDate = findDashaAtDate(kundali, milestone.date) || milestone.dashaAtEvent;

  const expectedIndicatorsEn: string[] = [
    `Primary house(s) [${rule.primaryHouses.join(', ')}] active via Dasha or lordship`,
    `Natural significator(s) [${rule.naturalKarakas.join(', ')}] favorably connected`,
    `Supporting house(s) [${rule.supportingHouses.join(', ')}] reinforced`
  ];

  const expectedIndicatorsHi: string[] = [
    `मुख्य भाव [${rule.primaryHouses.join(', ')}] दशा या भावेश द्वारा सक्रिय होना`,
    `नैसर्गिक कारक [${rule.naturalKarakas.join(', ')}] का अनुकूल संबंध`,
    `सहायक भाव [${rule.supportingHouses.join(', ')}] का समर्थन`
  ];

  const matchedIndicatorsEn: string[] = [];
  const matchedIndicatorsHi: string[] = [];
  const unmatchedIndicatorsEn: string[] = [];
  const unmatchedIndicatorsHi: string[] = [];

  if (!dashaAtDate || !dashaAtDate.mahadasha) {
    return {
      milestoneId: milestone.id,
      eventType: milestone.eventType,
      observedDate: milestone.date,
      expectedIndicatorsEn,
      expectedIndicatorsHi,
      matchedIndicatorsEn: [],
      matchedIndicatorsHi: [],
      unmatchedIndicatorsEn: ['Dasha cycle information unavailable for milestone date.'],
      unmatchedIndicatorsHi: ['घटना की तिथि के लिए दशा चक्र की जानकारी उपलब्ध नहीं है।'],
      calibrationAccuracyScore: 50,
      confidence: 'LOW',
      methodology: 'Deterministic Astrological Rule Matching (V5.19)',
      limitations: 'Historical milestone occurred outside the computed Dasha span or birth chart boundary.'
    };
  }

  const mdLord = dashaAtDate.mahadasha as PlanetName;
  const adLord = (dashaAtDate.antardasha || mdLord) as PlanetName;

  const mdHouses = getPlanetOwnedHouses(mdLord, lagnaSign);
  const adHouses = getPlanetOwnedHouses(adLord, lagnaSign);
  const activeHouses = Array.from(new Set([...mdHouses, ...adHouses]));

  const mdPl = kundali.planets?.find(p => p.name === mdLord);
  const adPl = kundali.planets?.find(p => p.name === adLord);
  if (mdPl) activeHouses.push(mdPl.house);
  if (adPl) activeHouses.push(adPl.house);

  const primaryMatches = activeHouses.filter(h => rule.primaryHouses.includes(h));
  const supportingMatches = activeHouses.filter(h => rule.supportingHouses.includes(h));
  const cautionMatches = activeHouses.filter(h => rule.cautionHouses.includes(h));
  const isKarakaMatched = rule.naturalKarakas.includes(mdLord) || rule.naturalKarakas.includes(adLord);

  let rawScore = 50;

  // 1. Primary House Activation
  if (primaryMatches.length > 0) {
    const points = Math.min(30, primaryMatches.length * 15);
    rawScore += points;
    matchedIndicatorsEn.push(`Active Dasha (${mdLord}-${adLord}) governs primary house(s) [${primaryMatches.join(', ')}].`);
    matchedIndicatorsHi.push(`सक्रिय दशा (${mdLord}-${adLord}) मुख्य भाव [${primaryMatches.join(', ')}] की अधिपति है।`);
  } else {
    unmatchedIndicatorsEn.push(`Neither Mahadasha lord (${mdLord}) nor Antardasha lord (${adLord}) directly governs primary house(s) [${rule.primaryHouses.join(', ')}].`);
    unmatchedIndicatorsHi.push(`न तो महादशा स्वामी (${mdLord}) और न ही अंतर्दशा स्वामी (${adLord}) मुख्य भाव [${rule.primaryHouses.join(', ')}] के स्वामी हैं।`);
  }

  // 2. Supporting House Activation
  if (supportingMatches.length > 0) {
    rawScore += Math.min(16, supportingMatches.length * 8);
    matchedIndicatorsEn.push(`Supporting house(s) [${supportingMatches.join(', ')}] energized under active Dasha.`);
    matchedIndicatorsHi.push(`सक्रिय दशा के अंतर्गत सहायक भाव [${supportingMatches.join(', ')}] को बल प्राप्त हुआ।`);
  }

  // 3. Karaka Alignment
  if (isKarakaMatched) {
    rawScore += 12;
    matchedIndicatorsEn.push(`Natural Karaka (${rule.naturalKarakas.join('/')}) was active as Mahadasha or Antardasha lord.`);
    matchedIndicatorsHi.push(`प्राकृतिक कारक (${rule.naturalKarakas.join('/')}) महादशा या अंतर्दशा स्वामी के रूप में सक्रिय था।`);
  } else {
    unmatchedIndicatorsEn.push(`Natural Karaka (${rule.naturalKarakas.join('/')}) was not the primary operating Dasha lord.`);
    unmatchedIndicatorsHi.push(`प्राकृतिक कारक (${rule.naturalKarakas.join('/')}) मुख्य परिचालन दशा स्वामी नहीं था।`);
  }

  // 4. Caution House Penalty
  if (cautionMatches.length > 0 && primaryMatches.length === 0) {
    rawScore -= cautionMatches.length * 12;
    unmatchedIndicatorsEn.push(`Active Dasha lords governed caution house(s) [${cautionMatches.join(', ')}] without direct primary house support.`);
    unmatchedIndicatorsHi.push(`सक्रिय दशा स्वामी प्रतिकूल भाव [${cautionMatches.join(', ')}] के अधिपति थे।`);
  }

  // 5. Verification status bonus
  if (milestone.verified) {
    rawScore += 4;
  }

  const calibrationAccuracyScore: V519Score = Math.max(15, Math.min(95, Math.round(rawScore)));
  const confidence: 'HIGH' | 'MEDIUM' | 'LOW' =
    calibrationAccuracyScore >= 75 && milestone.verified
      ? 'HIGH'
      : calibrationAccuracyScore >= 55
      ? 'MEDIUM'
      : 'LOW';

  return {
    milestoneId: milestone.id,
    eventType: milestone.eventType,
    observedDate: milestone.date,
    expectedIndicatorsEn,
    expectedIndicatorsHi,
    matchedIndicatorsEn,
    matchedIndicatorsHi,
    unmatchedIndicatorsEn,
    unmatchedIndicatorsHi,
    calibrationAccuracyScore,
    confidence,
    methodology: 'Deterministic Astrological Rule Matching (V5.19)',
    limitations: 'The Astrological Calibration Score measures structural correspondence between recorded historical events and classical astrological principles (Vimshottari Dasha and Bhava lords). It is an interpretive calibration metric, NOT an empirical or statistical proof of predictive causation.'
  };
}

/* ==========================================================================
   4. CALIBRATION SUMMARY AGGREGATION
   ========================================================================== */

/**
 * Aggregates individual milestone calibrations into a holistic chart calibration summary.
 */
export function calculateCalibrationSummary(
  milestones: readonly NativeLifeMilestone[],
  results: readonly CalibrationResult[]
): CalibrationSummary {
  const totalMilestones = milestones.length;
  const verifiedMilestones = milestones.filter(m => m.verified).length;

  if (totalMilestones === 0 || results.length === 0) {
    return {
      totalMilestones: 0,
      verifiedMilestones: 0,
      averageCalibrationScore: 50,
      strongestValidatedPatternsEn: ['No historical milestones recorded yet for calibration.'],
      strongestValidatedPatternsHi: ['अंशांकन के लिए अभी तक कोई ऐतिहासिक घटना दर्ज नहीं है।'],
      weakPatternsEn: [],
      weakPatternsHi: [],
      unresolvedPatternsEn: [],
      unresolvedPatternsHi: [],
      lastCalibratedAt: new Date().toISOString()
    };
  }

  const totalScore = results.reduce((sum, r) => sum + r.calibrationAccuracyScore, 0);
  const averageCalibrationScore = Math.max(15, Math.min(95, Math.round(totalScore / results.length)));

  const domainScores: Record<string, { total: number; count: number }> = {};
  for (const r of results) {
    if (!domainScores[r.eventType]) {
      domainScores[r.eventType] = { total: 0, count: 0 };
    }
    domainScores[r.eventType].total += r.calibrationAccuracyScore;
    domainScores[r.eventType].count += 1;
  }

  const strongestValidatedPatternsEn: string[] = [];
  const strongestValidatedPatternsHi: string[] = [];
  const weakPatternsEn: string[] = [];
  const weakPatternsHi: string[] = [];
  const unresolvedPatternsEn: string[] = [];
  const unresolvedPatternsHi: string[] = [];

  for (const [eventType, data] of Object.entries(domainScores)) {
    const avg = Math.round(data.total / data.count);
    const rule = getEventRuleDefinition(eventType as PredictiveEventType);
    if (avg >= 70) {
      strongestValidatedPatternsEn.push(`${rule.titleEn}: Strong historical correspondence (Avg ${avg}/100 across ${data.count} milestone(s)).`);
      strongestValidatedPatternsHi.push(`${rule.titleHi}: मजबूत ऐतिहासिक सामंजस्य (${data.count} घटनाओं में औसत ${avg}/100)।`);
    } else if (avg < 50) {
      weakPatternsEn.push(`${rule.titleEn}: Mixed/divergent correspondence with classical dasha rules (Avg ${avg}/100).`);
      weakPatternsHi.push(`${rule.titleHi}: शास्त्रीय दशा नियमों से भिन्नता/मिश्रित परिणाम (औसत ${avg}/100)।`);
    } else {
      unresolvedPatternsEn.push(`${rule.titleEn}: Moderate correspondence (Avg ${avg}/100) requires further historical context.`);
      unresolvedPatternsHi.push(`${rule.titleHi}: मध्यम सामंजस्य (${avg}/100), अतिरिक्त ऐतिहासिक संदर्भ अपेक्षित।`);
    }
  }

  return {
    totalMilestones,
    verifiedMilestones,
    averageCalibrationScore,
    strongestValidatedPatternsEn: strongestValidatedPatternsEn.length > 0 ? strongestValidatedPatternsEn : ['General correspondence within normal baseline range.'],
    strongestValidatedPatternsHi: strongestValidatedPatternsHi.length > 0 ? strongestValidatedPatternsHi : ['सामान्य मानकों के अनुरूप सामंजस्य।'],
    weakPatternsEn,
    weakPatternsHi,
    unresolvedPatternsEn,
    unresolvedPatternsHi,
    lastCalibratedAt: new Date().toISOString()
  };
}

/* ==========================================================================
   5. PURE LIFE MILESTONE PROFILE MANAGEMENT
   ========================================================================== */

/**
 * Creates an empty initial LongitudinalProfile for a given nativeId.
 */
export function createInitialLongitudinalProfile(nativeId: string): LongitudinalProfile {
  const now = new Date().toISOString();
  return {
    nativeId,
    milestones: [],
    remedyAdherenceLog: [],
    consultationSessions: [],
    calibrationSummary: {
      totalMilestones: 0,
      verifiedMilestones: 0,
      averageCalibrationScore: 50,
      strongestValidatedPatternsEn: ['No historical milestones recorded yet.'],
      strongestValidatedPatternsHi: ['अभी तक कोई ऐतिहासिक घटना दर्ज नहीं की गई है।'],
      weakPatternsEn: [],
      weakPatternsHi: [],
      unresolvedPatternsEn: [],
      unresolvedPatternsHi: [],
      lastCalibratedAt: now
    },
    predictionOutcomes: [],
    createdAt: now,
    updatedAt: now
  };
}

/**
 * Immutable function to add a life milestone. Strictly validates date format.
 * Automatically computes dasha and calibration if kundali is provided.
 */
export function addLifeMilestone(
  profile: LongitudinalProfile,
  milestoneInput: Omit<NativeLifeMilestone, 'id' | 'createdAt' | 'updatedAt' | 'dashaAtEvent' | 'calibrationAccuracyScore' | 'verified'> & {
    readonly id?: string;
    readonly dashaAtEvent?: DashaAtMilestone;
    readonly calibrationAccuracyScore?: V519Score;
    readonly verified?: boolean;
  },
  kundali?: CompleteKundali
): LongitudinalProfile {
  if (!isValidDateFormat(milestoneInput.date)) {
    throw new Error(`Invalid milestone date: "${milestoneInput.date}". Must be valid YYYY-MM-DD.`);
  }

  const id = milestoneInput.id || generateDeterministicId('ms', milestoneInput.date, profile.milestones.length);
  const now = new Date().toISOString();

  let dashaAtEvent: DashaAtMilestone = milestoneInput.dashaAtEvent || {
    mahadasha: 'Jupiter',
    antardasha: 'Saturn'
  };

  if (kundali) {
    const foundDasha = findDashaAtDate(kundali, milestoneInput.date);
    if (foundDasha) {
      dashaAtEvent = foundDasha;
    }
  }

  const temporaryMilestone: NativeLifeMilestone = {
    id,
    eventType: milestoneInput.eventType,
    eventTitle: milestoneInput.eventTitle || getEventRuleDefinition(milestoneInput.eventType).titleEn,
    date: milestoneInput.date,
    notes: milestoneInput.notes || '',
    dashaAtEvent,
    calibrationAccuracyScore: milestoneInput.calibrationAccuracyScore || 50,
    source: milestoneInput.source || 'USER_SUBMISSION',
    confidence: milestoneInput.confidence || 'MEDIUM',
    verified: Boolean(milestoneInput.verified),
    tags: milestoneInput.tags ? [...milestoneInput.tags] : [],
    createdAt: now,
    updatedAt: now
  };

  let calibrationScore = temporaryMilestone.calibrationAccuracyScore;
  if (kundali) {
    const calResult = calibrateMilestone(kundali, temporaryMilestone);
    calibrationScore = calResult.calibrationAccuracyScore;
  }

  const finalMilestone: NativeLifeMilestone = {
    ...temporaryMilestone,
    calibrationAccuracyScore: calibrationScore
  };

  const updatedMilestones = [...profile.milestones, finalMilestone];
  const sortedMilestones = sortMilestonesChronologically(updatedMilestones);

  let updatedSummary = profile.calibrationSummary;
  if (kundali) {
    const allResults = sortedMilestones.map(m => calibrateMilestone(kundali, m));
    updatedSummary = calculateCalibrationSummary(sortedMilestones, allResults);
  } else {
    updatedSummary = {
      ...profile.calibrationSummary,
      totalMilestones: sortedMilestones.length,
      verifiedMilestones: sortedMilestones.filter(m => m.verified).length,
      lastCalibratedAt: now
    };
  }

  return {
    ...profile,
    milestones: sortedMilestones,
    calibrationSummary: updatedSummary,
    updatedAt: now
  };
}

/**
 * Immutable function to update an existing life milestone.
 */
export function updateLifeMilestone(
  profile: LongitudinalProfile,
  milestoneId: string,
  updates: Partial<NativeLifeMilestone>,
  kundali?: CompleteKundali
): LongitudinalProfile {
  if (updates.date !== undefined && !isValidDateFormat(updates.date)) {
    throw new Error(`Invalid milestone date update: "${updates.date}". Must be valid YYYY-MM-DD.`);
  }

  const index = profile.milestones.findIndex(m => m.id === milestoneId);
  if (index === -1) return profile;

  const now = new Date().toISOString();
  const existing = profile.milestones[index];
  const merged: NativeLifeMilestone = {
    ...existing,
    ...updates,
    updatedAt: now
  };

  if (kundali && (updates.date !== undefined || updates.eventType !== undefined)) {
    const foundDasha = findDashaAtDate(kundali, merged.date);
    const dashaAtEvent = foundDasha || merged.dashaAtEvent;
    const calResult = calibrateMilestone(kundali, { ...merged, dashaAtEvent });
    (merged as any).dashaAtEvent = dashaAtEvent;
    (merged as any).calibrationAccuracyScore = calResult.calibrationAccuracyScore;
  }

  const updatedMilestones = profile.milestones.map((m, i) => i === index ? merged : m);
  const sortedMilestones = sortMilestonesChronologically(updatedMilestones);

  let updatedSummary = profile.calibrationSummary;
  if (kundali) {
    const allResults = sortedMilestones.map(m => calibrateMilestone(kundali, m));
    updatedSummary = calculateCalibrationSummary(sortedMilestones, allResults);
  }

  return {
    ...profile,
    milestones: sortedMilestones,
    calibrationSummary: updatedSummary,
    updatedAt: now
  };
}

/**
 * Immutable function to remove a life milestone.
 */
export function removeLifeMilestone(
  profile: LongitudinalProfile,
  milestoneId: string,
  kundali?: CompleteKundali
): LongitudinalProfile {
  const updatedMilestones = profile.milestones.filter(m => m.id !== milestoneId);
  if (updatedMilestones.length === profile.milestones.length) return profile;

  const now = new Date().toISOString();
  let updatedSummary = profile.calibrationSummary;

  if (kundali && updatedMilestones.length > 0) {
    const allResults = updatedMilestones.map(m => calibrateMilestone(kundali, m));
    updatedSummary = calculateCalibrationSummary(updatedMilestones, allResults);
  } else {
    updatedSummary = {
      ...profile.calibrationSummary,
      totalMilestones: updatedMilestones.length,
      verifiedMilestones: updatedMilestones.filter(m => m.verified).length,
      averageCalibrationScore: updatedMilestones.length === 0 ? 50 : profile.calibrationSummary.averageCalibrationScore,
      lastCalibratedAt: now
    };
  }

  return {
    ...profile,
    milestones: updatedMilestones,
    calibrationSummary: updatedSummary,
    updatedAt: now
  };
}

export function getMilestones(profile: LongitudinalProfile): readonly NativeLifeMilestone[] {
  return profile.milestones;
}

export function filterMilestonesByEventType(
  profile: LongitudinalProfile,
  eventType: PredictiveEventType
): readonly NativeLifeMilestone[] {
  return profile.milestones.filter(m => m.eventType === eventType);
}

export function sortMilestonesChronologically(
  milestones: readonly NativeLifeMilestone[]
): readonly NativeLifeMilestone[] {
  return [...milestones].sort((a, b) => a.date.localeCompare(b.date));
}

/* ==========================================================================
   6. PREDICTION OUTCOME VALIDATION ENGINE
   ========================================================================== */

/**
 * Pure function to register a prediction outcome.
 */
export function addPredictionOutcomeRecord(
  profile: LongitudinalProfile,
  recordInput: Omit<PredictionOutcomeRecord, 'loggedAt'> & { readonly loggedAt?: string }
): LongitudinalProfile {
  if (!isValidDateFormat(recordInput.predictedStartDate) || !isValidDateFormat(recordInput.predictedEndDate)) {
    throw new Error('Predicted start and end dates must be valid YYYY-MM-DD strings.');
  }
  if (recordInput.actualOutcomeDate && !isValidDateFormat(recordInput.actualOutcomeDate)) {
    throw new Error('Actual outcome date must be a valid YYYY-MM-DD string.');
  }

  const now = new Date().toISOString();
  const newRecord: PredictionOutcomeRecord = {
    ...recordInput,
    loggedAt: recordInput.loggedAt || now
  };

  const existingOutcomes = profile.predictionOutcomes || [];
  return {
    ...profile,
    predictionOutcomes: [...existingOutcomes, newRecord],
    updatedAt: now
  };
}

/**
 * Pure function to update a prediction outcome record.
 */
export function updatePredictionOutcomeRecord(
  profile: LongitudinalProfile,
  predictionId: string,
  updates: Partial<PredictionOutcomeRecord>
): LongitudinalProfile {
  const existingOutcomes = profile.predictionOutcomes || [];
  const index = existingOutcomes.findIndex(o => o.predictionId === predictionId);
  if (index === -1) return profile;

  if (updates.actualOutcomeDate && !isValidDateFormat(updates.actualOutcomeDate)) {
    throw new Error('Actual outcome date update must be a valid YYYY-MM-DD string.');
  }

  const now = new Date().toISOString();
  const updatedRecord: PredictionOutcomeRecord = {
    ...existingOutcomes[index],
    ...updates,
    updatedAt: now
  };

  const updatedOutcomes = existingOutcomes.map((o, i) => i === index ? updatedRecord : o);
  return {
    ...profile,
    predictionOutcomes: updatedOutcomes,
    updatedAt: now
  };
}

export function getPredictionOutcomes(profile: LongitudinalProfile): readonly PredictionOutcomeRecord[] {
  return profile.predictionOutcomes || [];
}

export function filterPredictionOutcomesByStatus(
  profile: LongitudinalProfile,
  status: PredictionOutcomeStatus
): readonly PredictionOutcomeRecord[] {
  return (profile.predictionOutcomes || []).filter(o => o.outcomeStatus === status);
}

export interface PredictionAccuracySummary {
  readonly total: number;
  readonly hitCount: number;
  readonly partialCount: number;
  readonly missedCount: number;
  readonly pendingCount: number;
  readonly unknownCount: number;
  readonly hitRate: number;
  readonly summaryEn: string;
  readonly summaryHi: string;
}

export function summarizePredictionAccuracy(
  outcomes: readonly PredictionOutcomeRecord[]
): PredictionAccuracySummary {
  const total = outcomes.length;
  if (total === 0) {
    return {
      total: 0,
      hitCount: 0,
      partialCount: 0,
      missedCount: 0,
      pendingCount: 0,
      unknownCount: 0,
      hitRate: 0,
      summaryEn: 'No prediction outcomes recorded yet for validation.',
      summaryHi: 'सत्यापन के लिए अभी तक कोई भविष्यवाणी परिणाम दर्ज नहीं है।'
    };
  }

  const hitCount = outcomes.filter(o => o.outcomeStatus === 'HIT').length;
  const partialCount = outcomes.filter(o => o.outcomeStatus === 'PARTIAL').length;
  const missedCount = outcomes.filter(o => o.outcomeStatus === 'MISSED').length;
  const pendingCount = outcomes.filter(o => o.outcomeStatus === 'PENDING').length;
  const unknownCount = outcomes.filter(o => o.outcomeStatus === 'UNKNOWN').length;

  const resolved = hitCount + partialCount + missedCount;
  const hitRate = resolved > 0 ? Math.round(((hitCount + partialCount * 0.5) / resolved) * 100) : 0;

  return {
    total,
    hitCount,
    partialCount,
    missedCount,
    pendingCount,
    unknownCount,
    hitRate,
    summaryEn: `Tracked ${total} forecast window(s): ${hitCount} Hit, ${partialCount} Partial, ${missedCount} Missed, ${pendingCount} Pending. Resolved Hit Rate: ${hitRate}%.`,
    summaryHi: `कुल ${total} भविष्यवाणी चक्र ट्रैक किए गए: ${hitCount} सटीक, ${partialCount} आंशिक, ${missedCount} अप्रभावी, ${pendingCount} प्रतीक्षारत। समाधान दर: ${hitRate}%।`
  };
}

/* ==========================================================================
   7. REMEDY ADHERENCE & FEEDBACK TRACKING
   ========================================================================== */

/**
 * Adds a new remedy adherence record to the native's profile.
 */
export function addRemedyRecord(
  profile: LongitudinalProfile,
  recordInput: Omit<RemedyAdherenceRecord, 'loggedAt'> & { readonly loggedAt?: string }
): LongitudinalProfile {
  if (!isValidDateFormat(recordInput.startDate)) {
    throw new Error('Remedy startDate must be a valid YYYY-MM-DD string.');
  }
  if (recordInput.endDate && !isValidDateFormat(recordInput.endDate)) {
    throw new Error('Remedy endDate must be a valid YYYY-MM-DD string.');
  }

  const now = new Date().toISOString();
  const record: RemedyAdherenceRecord = {
    ...recordInput,
    loggedAt: recordInput.loggedAt || now
  };

  return {
    ...profile,
    remedyAdherenceLog: [...profile.remedyAdherenceLog, record],
    updatedAt: now
  };
}

/**
 * Updates an existing remedy adherence record.
 */
export function updateRemedyRecord(
  profile: LongitudinalProfile,
  remedyId: string,
  updates: Partial<RemedyAdherenceRecord>
): LongitudinalProfile {
  const index = profile.remedyAdherenceLog.findIndex(r => r.remedyId === remedyId);
  if (index === -1) return profile;

  const now = new Date().toISOString();
  const updatedRecord: RemedyAdherenceRecord = {
    ...profile.remedyAdherenceLog[index],
    ...updates
  };

  const updatedLog = profile.remedyAdherenceLog.map((r, i) => i === index ? updatedRecord : r);
  return {
    ...profile,
    remedyAdherenceLog: updatedLog,
    updatedAt: now
  };
}

export function markRemedyCompleted(
  profile: LongitudinalProfile,
  remedyId: string,
  observedOutcomeEn?: string,
  observedOutcomeHi?: string
): LongitudinalProfile {
  return updateRemedyRecord(profile, remedyId, {
    status: 'COMPLETED',
    endDate: new Date().toISOString().split('T')[0],
    observedOutcomeEn: observedOutcomeEn || 'Native reported completion of prescribed remedy cycle.',
    observedOutcomeHi: observedOutcomeHi || 'जातक ने निर्धारित उपाय चक्र पूर्ण करने की सूचना दी।'
  });
}

export function markRemedyPaused(
  profile: LongitudinalProfile,
  remedyId: string,
  notes?: string
): LongitudinalProfile {
  return updateRemedyRecord(profile, remedyId, {
    status: 'PAUSED',
    notes: notes || 'Remedy temporarily paused by native.'
  });
}

export function markRemedyDiscontinued(
  profile: LongitudinalProfile,
  remedyId: string,
  reason?: string
): LongitudinalProfile {
  return updateRemedyRecord(profile, remedyId, {
    status: 'DISCONTINUED',
    endDate: new Date().toISOString().split('T')[0],
    notes: reason || 'Remedy discontinued.'
  });
}

export function recordRemedyFeedback(
  profile: LongitudinalProfile,
  remedyId: string,
  feedbackScore: number,
  notes?: string
): LongitudinalProfile {
  const boundedScore: V519Score = Math.max(15, Math.min(95, Math.round(feedbackScore)));
  return updateRemedyRecord(profile, remedyId, {
    nativeFeedbackScore: boundedScore,
    notes: notes || undefined
  });
}

export interface RemedyOutcomesSummary {
  readonly total: number;
  readonly activeCount: number;
  readonly completedCount: number;
  readonly pausedCount: number;
  readonly discontinuedCount: number;
  readonly avgFeedbackScore: number;
  readonly reportedOutcomesSummaryEn: readonly string[];
  readonly reportedOutcomesSummaryHi: readonly string[];
}

export function summarizeRemedyOutcomes(
  remedyLog: readonly RemedyAdherenceRecord[]
): RemedyOutcomesSummary {
  const total = remedyLog.length;
  if (total === 0) {
    return {
      total: 0,
      activeCount: 0,
      completedCount: 0,
      pausedCount: 0,
      discontinuedCount: 0,
      avgFeedbackScore: 0,
      reportedOutcomesSummaryEn: ['No remedy records logged.'],
      reportedOutcomesSummaryHi: ['कोई उपाय रिकॉर्ड दर्ज नहीं है।']
    };
  }

  const activeCount = remedyLog.filter(r => r.status === 'ACTIVE').length;
  const completedCount = remedyLog.filter(r => r.status === 'COMPLETED').length;
  const pausedCount = remedyLog.filter(r => r.status === 'PAUSED').length;
  const discontinuedCount = remedyLog.filter(r => r.status === 'DISCONTINUED').length;

  const scoredRecords = remedyLog.filter(r => typeof r.nativeFeedbackScore === 'number');
  const avgFeedbackScore = scoredRecords.length > 0
    ? Math.round(scoredRecords.reduce((sum, r) => sum + (r.nativeFeedbackScore || 0), 0) / scoredRecords.length)
    : 0;

  const reportedOutcomesSummaryEn: string[] = remedyLog
    .filter(r => Boolean(r.observedOutcomeEn))
    .map(r => `${r.remedyNameEn}: ${r.observedOutcomeEn}`);

  const reportedOutcomesSummaryHi: string[] = remedyLog
    .filter(r => Boolean(r.observedOutcomeHi))
    .map(r => `${r.remedyNameHi}: ${r.observedOutcomeHi}`);

  return {
    total,
    activeCount,
    completedCount,
    pausedCount,
    discontinuedCount,
    avgFeedbackScore,
    reportedOutcomesSummaryEn: reportedOutcomesSummaryEn.length > 0 ? reportedOutcomesSummaryEn : ['No specific observed outcomes reported.'],
    reportedOutcomesSummaryHi: reportedOutcomesSummaryHi.length > 0 ? reportedOutcomesSummaryHi : ['कोई विशेष प्रेक्षित परिणाम दर्ज नहीं है।']
  };
}

/* ==========================================================================
   8. CONSULTATION HISTORY & MULTI-SESSION MEMORY
   ========================================================================== */

/**
 * Creates and appends a consultation session to the native profile.
 */
export function createConsultationSession(
  profile: LongitudinalProfile,
  sessionInput: Omit<ConsultationSession, 'sessionId'> & { readonly sessionId?: string }
): { profile: LongitudinalProfile; newSession: ConsultationSession } {
  if (!isValidDateFormat(sessionInput.date)) {
    throw new Error('Consultation session date must be a valid YYYY-MM-DD string.');
  }

  const sessionId = sessionInput.sessionId || generateDeterministicId('sess', sessionInput.date, profile.consultationSessions.length);
  const newSession: ConsultationSession = {
    ...sessionInput,
    sessionId
  };

  const updatedProfile: LongitudinalProfile = {
    ...profile,
    consultationSessions: [...profile.consultationSessions, newSession],
    updatedAt: new Date().toISOString()
  };

  return { profile: updatedProfile, newSession };
}

/**
 * Closes a consultation session with outcome notes.
 */
export function closeConsultationSession(
  profile: LongitudinalProfile,
  sessionId: string,
  outcomeNotes?: string
): LongitudinalProfile {
  const index = profile.consultationSessions.findIndex(s => s.sessionId === sessionId);
  if (index === -1) return profile;

  const session = profile.consultationSessions[index];
  const updatedSession: ConsultationSession = {
    ...session,
    outcomeNotes: outcomeNotes || session.outcomeNotes
  };

  const updatedSessions = profile.consultationSessions.map((s, i) => i === index ? updatedSession : s);
  return {
    ...profile,
    consultationSessions: updatedSessions,
    updatedAt: new Date().toISOString()
  };
}

export function getRecentConsultationSessions(
  profile: LongitudinalProfile,
  limit: number = 5
): readonly ConsultationSession[] {
  const sorted = [...profile.consultationSessions].sort((a, b) => b.date.localeCompare(a.date));
  return sorted.slice(0, limit);
}

export function getEventSpecificHistory(
  profile: LongitudinalProfile,
  eventType: PredictiveEventType
): {
  milestones: readonly NativeLifeMilestone[];
  priorPredictions: readonly PredictiveEventWindow[];
  pastOutcomes: readonly PredictionOutcomeRecord[];
} {
  const milestones = filterMilestonesByEventType(profile, eventType);
  const pastOutcomes = (profile.predictionOutcomes || []).filter(o => o.eventType === eventType);

  const priorPredictions: PredictiveEventWindow[] = [];
  for (const session of profile.consultationSessions) {
    for (const pred of session.predictedEvents) {
      if (pred.eventType === eventType) {
        priorPredictions.push(pred);
      }
    }
  }

  return {
    milestones,
    priorPredictions,
    pastOutcomes
  };
}

export function getPriorRecommendations(
  profile: LongitudinalProfile
): readonly { sessionId: string; date: string; recommendationsEn: readonly string[]; recommendationsHi: readonly string[] }[] {
  return profile.consultationSessions.map(s => ({
    sessionId: s.sessionId,
    date: s.date,
    recommendationsEn: s.recommendationsEn,
    recommendationsHi: s.recommendationsHi
  }));
}

export function getPriorPredictions(
  profile: LongitudinalProfile
): readonly PredictiveEventWindow[] {
  const list: PredictiveEventWindow[] = [];
  for (const s of profile.consultationSessions) {
    list.push(...s.predictedEvents);
  }
  return list;
}

/**
 * Creates an empty ConsultationMemory structure for a native.
 */
export function createConsultationMemory(nativeId: string): ConsultationMemory {
  return {
    nativeId,
    sessions: [],
    memoryItems: []
  };
}

/**
 * Appends a verified factual or astrological memory item to ConsultationMemory.
 */
export function appendConsultationMemoryItem(
  memory: ConsultationMemory,
  itemInput: Omit<ConsultationMemoryItem, 'id' | 'recordedAt'> & {
    readonly id?: string;
    readonly recordedAt?: string;
  }
): ConsultationMemory {
  const id = itemInput.id || generateDeterministicId('mem', memory.nativeId, memory.memoryItems.length);
  const now = new Date().toISOString();

  const newItem: ConsultationMemoryItem = {
    id,
    sessionId: itemInput.sessionId,
    type: itemInput.type,
    contentEn: itemInput.contentEn,
    contentHi: itemInput.contentHi,
    recordedAt: itemInput.recordedAt || now,
    verified: Boolean(itemInput.verified)
  };

  return {
    ...memory,
    memoryItems: [...memory.memoryItems, newItem]
  };
}

export function getConsultationMemoryItems(
  memory: ConsultationMemory,
  type?: ConsultationMemoryItemType
): readonly ConsultationMemoryItem[] {
  if (!type) return memory.memoryItems;
  return memory.memoryItems.filter(m => m.type === type);
}

/* ==========================================================================
   9. LONGITUDINAL CONTEXT PACKAGE SERIALIZER
   ========================================================================== */

export interface LongitudinalContextFilter {
  readonly eventType?: PredictiveEventType;
  readonly maxMilestones?: number;
  readonly maxSessions?: number;
}

export interface LongitudinalContextPackage {
  readonly nativeId: string;
  readonly milestoneHighlightsEn: readonly string[];
  readonly milestoneHighlightsHi: readonly string[];
  readonly calibrationScore: V519Score;
  readonly validatedStrengthsEn: readonly string[];
  readonly validatedStrengthsHi: readonly string[];
  readonly activeRemediesEn: readonly string[];
  readonly activeRemediesHi: readonly string[];
  readonly recentConsultationThemesEn: readonly string[];
  readonly recentConsultationThemesHi: readonly string[];
  readonly predictionHitRatePercent?: number;
  readonly limitationsDisclaimer: string;
}

/**
 * Serializes a native's longitudinal profile into a compact, privacy-safe context package.
 * Zero external calls, zero PII exposure, pure deterministic representation.
 */
export function getLongitudinalContext(
  profile: LongitudinalProfile,
  filter?: LongitudinalContextFilter
): LongitudinalContextPackage {
  const maxM = filter?.maxMilestones || 6;
  const maxS = filter?.maxSessions || 3;

  let filteredMilestones = profile.milestones;
  if (filter?.eventType) {
    filteredMilestones = filteredMilestones.filter(m => m.eventType === filter.eventType);
  }
  const topMilestones = filteredMilestones.slice(-maxM);

  const milestoneHighlightsEn = topMilestones.map(
    m => `${m.eventTitle || m.eventType} on ${m.date} (Dasha: ${m.dashaAtEvent.mahadasha}-${m.dashaAtEvent.antardasha}, Score: ${m.calibrationAccuracyScore}/100)`
  );
  const milestoneHighlightsHi = topMilestones.map(
    m => `${m.eventTitle || m.eventType} दिनांक ${m.date} (दशा: ${m.dashaAtEvent.mahadasha}-${m.dashaAtEvent.antardasha}, सामंजस्य: ${m.calibrationAccuracyScore}/100)`
  );

  const activeRemedies = profile.remedyAdherenceLog.filter(r => r.status === 'ACTIVE');
  const activeRemediesEn = activeRemedies.map(r => `${r.remedyNameEn} (Since: ${r.startDate})`);
  const activeRemediesHi = activeRemedies.map(r => `${r.remedyNameHi} (प्रारंभ: ${r.startDate})`);

  const recentSessions = profile.consultationSessions.slice(-maxS);
  const recentConsultationThemesEn = recentSessions.map(s => `${s.date}: ${s.chiefQuestion}`);
  const recentConsultationThemesHi = recentSessions.map(s => `${s.date}: ${s.chiefQuestion}`);

  const predOutcomes = profile.predictionOutcomes || [];
  const accuracy = predOutcomes.length > 0 ? summarizePredictionAccuracy(predOutcomes).hitRate : undefined;

  return {
    nativeId: profile.nativeId,
    milestoneHighlightsEn,
    milestoneHighlightsHi,
    calibrationScore: profile.calibrationSummary.averageCalibrationScore,
    validatedStrengthsEn: profile.calibrationSummary.strongestValidatedPatternsEn,
    validatedStrengthsHi: profile.calibrationSummary.strongestValidatedPatternsHi,
    activeRemediesEn,
    activeRemediesHi,
    recentConsultationThemesEn,
    recentConsultationThemesHi,
    predictionHitRatePercent: accuracy,
    limitationsDisclaimer: 'Longitudinal context represents user-reported historical records and astrological calibration models. It does not establish clinical or empirical proof of causality.'
  };
}
